import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'immutable';
import SvgIcon from '@mui/material/SvgIcon';

import { canPlaceApprovalStampSelector } from '@/domain/approvals';
import { rtlEnable } from '@/domain/env';
import { isOpenAFPanelSelector, setUiApprovalStampPositionAction } from '@/domain/ui';
import { UiApprovalStampPositionFactory } from '@/domain/ui/helpers';
import type { PdfPageBox } from '@/pages/document/DocumentViewer/DocumentFileView/pageGeometry';
import {
  type DisplayedApprovalStampOffset,
  approvalStampCorners,
  approvalStampSize,
  clampApprovalStampOffset,
  defaultApprovalStampOffset,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/ApprovalStampPlacement/approvalStampGeometry';
import {
  ApprovalStampMarker,
  ApprovalStampPlacementArea,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/ApprovalStampPlacement/StyledComponents';
import { ReactComponent as StampSVG } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/ApprovalStampPlacement/stamp.svg';

type Props = {
  /** The page as shown, in screen pixels. */
  pageWidth: number;
  pageHeight: number;
  pdfPageBox: PdfPageBox;
  /** The rotation the user turned the page to; the server turns the stamp with it. */
  userRotation: number;
  onDraggingChange: (isDragging: boolean) => void;
};

type Drag = { pointerX: number; pointerY: number; startOffset: DisplayedApprovalStampOffset };

type StampProps = Props & { isRtl: boolean };

// A Ctrl or Cmd press on the page starts a selection of its words, which the stamp keeps out of reach; a plain press
// still reaches the page, which moves.
const keepWordsSelectionOff = (event: PointerEvent<HTMLElement>) => {
  if (event.ctrlKey || event.metaKey) event.stopPropagation();
};

const DraggableApprovalStamp = ({
  pageWidth,
  pageHeight,
  pdfPageBox,
  userRotation,
  isRtl,
  onDraggingChange,
}: StampProps) => {
  const dispatch = useDispatch();
  const page = useMemo(() => ({ width: pageWidth, height: pageHeight }), [pageWidth, pageHeight]);
  const stamp = useMemo(() => approvalStampSize(page), [page]);
  const [offset, setOffset] = useState(() => defaultApprovalStampOffset(page, stamp, isRtl));
  const [isDragging, setIsDragging] = useState(false);
  const drag = useRef<Drag | null>(null);

  useEffect(() => {
    if (!isDragging) return undefined;
    onDraggingChange(true);
    return () => onDraggingChange(false);
  }, [isDragging, onDraggingChange]);

  useEffect(() => {
    if (isDragging) return;
    const { ll, ur } = approvalStampCorners(offset, page, stamp, pdfPageBox, isRtl);
    dispatch(
      setUiApprovalStampPositionAction(
        UiApprovalStampPositionFactory({ ll: List(ll), ur: List(ur), rotation: userRotation }),
      ),
    );
  }, [dispatch, isDragging, offset, page, stamp, pdfPageBox, isRtl, userRotation]);

  // The activation sends the stored position; a document that never showed the stamp must not send another one's.
  useEffect(
    () => () => {
      dispatch(setUiApprovalStampPositionAction(null));
    },
    [dispatch],
  );

  const startDrag = (event: PointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerX: event.clientX, pointerY: event.clientY, startOffset: offset };
    setIsDragging(true);
  };

  const moveDrag = (event: PointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    const { pointerX, pointerY, startOffset } = drag.current;
    const moved = { x: startOffset.x + event.clientX - pointerX, y: startOffset.y + event.clientY - pointerY };
    setOffset(clampApprovalStampOffset(moved, page, stamp, isRtl));
  };

  const endDrag = () => {
    drag.current = null;
    setIsDragging(false);
  };

  return (
    <ApprovalStampPlacementArea onPointerDown={keepWordsSelectionOff}>
      <ApprovalStampMarker
        role="button"
        tabIndex={0}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onLostPointerCapture={endDrag}
        style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
      >
        <SvgIcon component={StampSVG} inheritViewBox sx={{ width: stamp.width, height: stamp.height }} />
      </ApprovalStampMarker>
    </ApprovalStampPlacementArea>
  );
};

// A new page size, rotation or direction starts the stamp again from its corner.
const ApprovalStampPlacement = (props: Props) => {
  const isPanelOpen = useSelector(isOpenAFPanelSelector);
  const canPlace = useSelector(canPlaceApprovalStampSelector);
  const isRtl: boolean = useSelector(rtlEnable);

  if (!isPanelOpen || !canPlace) return null;
  const { pageWidth, pageHeight, userRotation } = props;
  return (
    <DraggableApprovalStamp key={`${pageWidth}x${pageHeight}/${userRotation}/${isRtl}`} {...props} isRtl={isRtl} />
  );
};

export default ApprovalStampPlacement;
